import React, { ReactNode, useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { DefaultValues, FieldValues, FormProvider, Path, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowLeft, HiOutlineArrowRight } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

import { ComponentField, DisplayField, FieldType, FormConfigField } from "@/types/DynamicForm.type";
import Button from "@/components/atoms/Button";
import Modal from "@/components/molecules/Modal";
import LeaveWithoutSavingModal from "@/components/molecules/predictive/LeaveWithoutSavingModal";

import {
  DynamicFormCheckboxes,
  DynamicFormComboBoxMultiple,
  DynamicFormCompanies,
  DynamicFormHeader,
  DynamicFormIndustries,
  DynamicFormInput,
  DynamicFormLocationSelect,
  DynamicFormNaceSelect,
  DynamicFormRegionSelect,
  DynamicFormSubscriptionSelect,
  DynamicFormTextarea,
  DynamicFormWarning,
} from "./fields";

type PropsFor<F extends FieldType> = F extends "header" | "warning"
  ? DisplayField<F>
  : Extract<ComponentField, { fieldType: F }>;

const COMPONENT_MAP: { [F in FieldType]: React.FC<PropsFor<F>> } = {
  input: DynamicFormInput,
  textarea: DynamicFormTextarea,
  header: DynamicFormHeader,
  warning: DynamicFormWarning,
  industries: DynamicFormIndustries,
  naceSelect: DynamicFormNaceSelect,
  companies: DynamicFormCompanies,
  subscriptionSelect: DynamicFormSubscriptionSelect,
  comboBoxMultiple: DynamicFormComboBoxMultiple,
  checkboxes: DynamicFormCheckboxes,
  locationSelect: DynamicFormLocationSelect,
  regionSelect: DynamicFormRegionSelect,
};

type DynamicFormProps<T extends FieldValues = FieldValues> = {
  /** Config map containing the field definitions */
  config: FormConfigField[];
  defaultValues?: DefaultValues<T>;
  onSubmit: SubmitHandler<T>;
  className?: string;
  children?: ReactNode;
  onStepChanged?: (step: number) => void;
  onSaveDraft?: (data: T) => void;
  submitBtnText?: string;
  draftBtnText?: string;
  isPending?: boolean;
  pageStart?: number;
};

// TODO Scroll to top on step change, guess need to rework parent layout to allow that
const DynamicForm = <T extends FieldValues = FieldValues>(props: DynamicFormProps<T>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leaveModalShown, setLeaveModalShown] = useState(false);
  const {
    config,
    defaultValues,
    onSubmit,
    className = "",
    children,
    onStepChanged,
    onSaveDraft,
    submitBtnText,
    draftBtnText,
    isPending = false,
    pageStart = 0, // For multi‑step forms, starting with 0
  } = props;

  const methods = useForm<T>({
    defaultValues: defaultValues,
    mode: "onBlur",
  });

  const explicitSteps = useMemo<number[]>(() => {
    const s = new Set<number>();
    config.forEach((f) => typeof f.step === "number" && s.add(f.step));
    return Array.from(s).sort((a, b) => a - b);
  }, [config]);

  const isExplicitMultiStep = explicitSteps.length > 0;
  const firstExplicitStep = explicitSteps[0];

  /** true ↠ we have multiple *visible* screens (either explicit or review) */
  const isMultiStep = isExplicitMultiStep;

  /** How many screens are dedicated to *fields* (not counting review) */
  const fieldScreens = isExplicitMultiStep ? explicitSteps.length : 1;

  const [currentScreen, setCurrentScreen] = useState(pageStart); // 0‑based visible index

  // Determine which field configs to show on current screen
  const visibleFields: FormConfigField[] = isExplicitMultiStep
    ? config.filter((f) => (f.step ?? firstExplicitStep) === explicitSteps[currentScreen])
    : config;

  const { handleSubmit, trigger, formState } = methods;

  const goToNextStep = async () => {
    // If single‑page without review, do full submit
    if (!isMultiStep) {
      await handleSubmit(onSubmit)();
      return;
    }

    // Validate current screen (always, including review, because fields editable)
    const interactive = visibleFields.filter((f): f is ComponentField => "name" in f);
    const names = interactive.map((f) => f.name as Path<T>[][number]);

    const isValid = await trigger(names);
    if (!isValid) return;

    // If on last screen now -> submit
    const lastScreen = fieldScreens - 1;
    if (currentScreen === lastScreen) {
      await handleSubmit(onSubmit)();
      return;
    }

    // Otherwise, advance
    const next = currentScreen + 1;
    setCurrentScreen(next);
    onStepChanged?.(next + 1);
  };

  const goToPrevStep = useCallback(() => {
    // If first screen -> confirm leave or navigate back
    if (currentScreen === 0) {
      if (formState.isDirty) setLeaveModalShown(true);
      else navigate(-1);
      return;
    }

    const prev = currentScreen - 1;
    setCurrentScreen(prev);
    onStepChanged?.(prev + 1);
  }, [currentScreen, formState.isDirty, onStepChanged, navigate]);

  const goBack = () => navigate(-1);

  const saveDraft = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const data = methods.getValues();
    onSaveDraft?.(data as T);
  };

  /** Stop Enter from submitting except inside a <textarea> */
  const handleKeyDown: React.KeyboardEventHandler<HTMLFormElement> = (e) => {
    if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
      e.preventDefault();
    }
  };

  const renderFieldGroup = (fields: FormConfigField[], dense: boolean) => (
    <div className={clsx("flex flex-col", dense ? "gap-10" : "gap-12")}>
      {fields.map((field, index) => {
        const fieldType = field.fieldType;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Component = COMPONENT_MAP[fieldType] as React.ComponentType<any>;
        const key = "name" in field ? field.name : `display-${field.fieldType}-${index}`;

        return <Component key={key} {...field} />;
      })}
    </div>
  );

  const renderDefaultNav = () => {
    const submitLabel = submitBtnText || t("submit");
    const saveDraftLabel = draftBtnText || t("saveDraft");
    const nextLabel = t("next");
    const backLabel = t("back");

    if (!isMultiStep) {
      return (
        <div className="flex justify-between gap-2">
          <Button type="button" label={t("cancel")} color="natural" onClick={goBack} />
          <Button type="submit" isLoading={formState.isSubmitting || isPending} label={submitLabel} />
        </div>
      );
    }

    const computedNextLabel = currentScreen === fieldScreens - 1 ? submitLabel : nextLabel;

    return (
      <div className="flex items-center justify-between gap-4">
        <Button
          type="button"
          label={currentScreen === 0 ? t("cancel") : backLabel}
          color="natural"
          onClick={goToPrevStep}
          leadingIcon={currentScreen > 0 ? <HiOutlineArrowLeft className="size-4" /> : undefined}
        />

        <div className="flex gap-2">
          {onSaveDraft && currentScreen > 0 && (
            <Button type="button" variants="secondary" label={saveDraftLabel} onClick={saveDraft} />
          )}
          <Button
            type="button"
            label={computedNextLabel}
            isLoading={formState.isSubmitting || isPending}
            trailingIcon={computedNextLabel === nextLabel ? <HiOutlineArrowRight className="size-4" /> : undefined}
            onClick={goToNextStep}
          />
        </div>
      </div>
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={handleKeyDown}
        className={clsx("flex flex-col gap-6 pb-40", className)}
      >
        {renderFieldGroup(visibleFields, true)}
        {children ?? renderDefaultNav()}
        <Modal
          onClose={setLeaveModalShown}
          isOpen={leaveModalShown}
          content={LeaveWithoutSavingModal}
          contentProps={{
            onDiscard: goBack,
          }}
        />
      </form>
    </FormProvider>
  );
};

export default DynamicForm;
